import React, { useState, useCallback } from 'react';
import { TableCell, TextField, Select, MenuItem, Box, Chip, Button, Tooltip, CircularProgress, Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions, Popover, Typography, IconButton, InputAdornment } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LockIcon from '@mui/icons-material/Lock';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { formattedDate } from '../sharedUtils';
import { getDropdownOptions, conditionToValueMap, conditionDisplayLabel } from '../productEditConstants';
import { SHOPIFY_CATEGORY_NAMES } from './shopifyCategoryOptions';

/**
 * A table cell that renders a value when idle and switches to an input on click.
 * Calls onSave(newValue) on blur or Enter.
 *
 * Props:
 *  column     — ColumnDef
 *  value      — current cell value (raw)
 *  product    — full product object (needed for dropdown options)
 *  onSave     — (newValue: string) => void
 *  onClick    — optional click handler (used for computed/readonly cells like Body HTML)
 *  lockedReason — when set, the cell is read-only for this row and shows a lock with this tooltip
 */
export default function InlineEditCell({ column, value, product, onSave, onClick, onPost, onUpdate, onDelete, isPending = false, dirty = false, editable: editableProp = true, lockedReason = null }) {
  const editable = editableProp && !lockedReason;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [dimsAnchorEl, setDimsAnchorEl] = useState(null);

  const isCategoryType = column.type === 'shopify-category' || column.type === 'ebay-category';
  // eBay/AI category editing is disabled for now — changing it triggers a reprocessing
  // flow that isn't reliable yet. Shopify Category (a separate taxonomy) is unaffected.
  const isCategoryReadOnly = column.type === 'ebay-category';
  // Dimensions come from AI enhancement and aren't normally user-editable, but if a
  // product has none yet we let the user fill them in by hand.
  const isDimensionsField = column.fieldPath === 'gptDimensionsData';
  // Columns can carry their own picklist (e.g. a customer's own Condition scale)
  // instead of the built-in eBay condition / dropdown options.
  const customOptions = Array.isArray(column.options) && column.options.length ? column.options : null;
  const isQuantityField = column.fieldPath === 'quantity';

  // Normalizes any raw cell value (string, number, null) to the string an edit draft holds.
  const toDraftString = (v) => (typeof v === 'string' ? v.replace(/^\$/, '') : String(v ?? ''));

  const startEdit = useCallback((e) => {
    if (!editable) return;
    if (isDimensionsField && !column.readOnly) {
      setDimsAnchorEl(e.currentTarget);
      return;
    }
    if (column.readOnly || isCategoryReadOnly || column.type === 'computed' || column.type === 'image') {
      if (onClick) onClick();
      return;
    }
    setDraft(toDraftString(value));
    setEditing(true);
  }, [column, value, onClick, isDimensionsField, isCategoryReadOnly, editable]);

  const commitEdit = useCallback(() => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed !== toDraftString(value)) {
      onSave(trimmed);
    }
  }, [draft, value, onSave]);

  const commitQuantity = useCallback((next) => {
    const clamped = String(Math.max(0, next));
    setDraft(clamped);
    if (clamped !== toDraftString(value)) onSave(clamped);
  }, [value, onSave]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.target.blur(); }
    if (e.key === 'Escape') { setEditing(false); }
  };

  // ── Render helpers ──────────────────────────────────────────────────────────

  if (column.type === 'image') {
    const urls = Array.isArray(value) ? value : [];
    return (
      <TableCell sx={{ p: 0.5, width: column.width || 80 }}>
        {urls[0] ? (
          <Box
            component="img"
            src={urls[0]}
            alt=""
            sx={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 1 }}
          />
        ) : (
          <Box sx={{ width: 60, height: 60, bgcolor: 'grey.100', borderRadius: 1 }} />
        )}
      </TableCell>
    );
  }

  if (column.type === 'computed') {
    return (
      <TableCell sx={{ width: column.width, py: 0.75, px: 1 }}>
        <ComputedCellContent column={column} value={value} product={product} onClick={onClick} onPost={onPost} onUpdate={onUpdate} onDelete={onDelete} isPending={isPending} />
      </TableCell>
    );
  }

  if (editing) {
    if (column.type === 'ebay-category') {
      const options = product?.gptCategories?.allPotentialCategories || [];
      return (
        <TableCell sx={{ p: 0.5, width: column.width, minWidth: 200 }}>
          <Autocomplete
            freeSolo
            autoFocus
            open
            size="small"
            options={options}
            value={draft}
            onInputChange={(_, val) => setDraft(val)}
            onChange={(_, val) => {
              if (val !== null) {
                setEditing(false);
                if (val !== (value || '')) onSave(val);
              }
            }}
            onClose={() => setEditing(false)}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                size="small"
                onBlur={() => {
                  setEditing(false);
                  if (draft !== (value || '')) onSave(draft);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setEditing(false);
                    if (draft !== (value || '')) onSave(draft);
                  } else if (e.key === 'Escape') {
                    setEditing(false);
                  }
                }}
              />
            )}
            ListboxProps={{ sx: { maxHeight: 320, fontSize: '0.82rem' } }}
          />
        </TableCell>
      );
    }
    if (column.type === 'select' || column.type === 'shopify-category') {
      const isConditionColumn = column.id === 'condition';
      const isCondition = isConditionColumn && !customOptions;
      // Keep a current value that isn't in the picklist selectable, so opening
      // the editor never silently blanks it.
      const listOptions = customOptions
        ? (value && !customOptions.includes(value) ? [value, ...customOptions] : customOptions)
        : null;
      const conditionEntries = value && !conditionToValueMap[value]
        ? [[value, conditionDisplayLabel(value)], ...Object.entries(conditionToValueMap)]
        : Object.entries(conditionToValueMap);
      // Condition values are raw eBay enums — always show the plain-English label.
      const optionLabel = (opt) => (isConditionColumn ? conditionDisplayLabel(opt) : opt);
      const handleSelectChange = (e) => {
        const selected = e.target.value;
        setEditing(false);
        if (selected !== (value || '')) onSave(selected);
      };
      return (
        <TableCell sx={{ p: 0.5, width: column.width, minWidth: 200 }}>
          <Select
            autoFocus
            open
            size="small"
            value={draft}
            onChange={handleSelectChange}
            onClose={() => setEditing(false)}
            fullWidth
            MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
            renderValue={(selected) => optionLabel(selected)}
          >
            {isCondition
              ? conditionEntries.map(([rawKey, label]) => (
                  <MenuItem key={rawKey} value={rawKey} sx={{ fontSize: '0.82rem', whiteSpace: 'normal' }}>{label}</MenuItem>
                ))
              : (listOptions || (column.type === 'shopify-category' ? SHOPIFY_CATEGORY_NAMES : getDropdownOptions(column.label, product || {}))).map((opt) => (
                  <MenuItem key={opt} value={opt} sx={{ fontSize: '0.82rem', whiteSpace: 'normal' }}>{optionLabel(opt)}</MenuItem>
                ))}
          </Select>
        </TableCell>
      );
    }
    if (isQuantityField) {
      const draftNum = parseInt(draft, 10) || 0;
      return (
        <TableCell sx={{ p: 0.5, width: column.width, minWidth: 100 }}>
          <TextField
            autoFocus
            size="small"
            type="number"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={handleKeyDown}
            fullWidth
            inputProps={{ min: 0, style: { textAlign: 'center' } }}
            InputProps={{
              sx: { fontSize: '0.85rem' },
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => commitQuantity(draftNum - 1)}>
                    <RemoveIcon fontSize="inherit" />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => commitQuantity(draftNum + 1)}>
                    <AddIcon fontSize="inherit" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </TableCell>
      );
    }
    return (
      <TableCell sx={{ p: 0.5, width: column.width, minWidth: 100 }}>
        <TextField
          autoFocus
          size="small"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={handleKeyDown}
          fullWidth
          multiline={column.type === 'text'}
          maxRows={4}
          InputProps={{ sx: { fontSize: '0.85rem' } }}
        />
      </TableCell>
    );
  }

  // Display mode
  const isEditableDimensionsCell = isDimensionsField && !column.readOnly;
  const isEditableCell = editable && (!column.readOnly || isEditableDimensionsCell) && !isCategoryReadOnly;
  const displayValue = isDimensionsField
    ? (formatDimensions(value) || <span style={{ color: editable ? '#1976d2' : '#bbb' }}>+ Add dimensions</span>)
    : lockedReason
      ? (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          {formatDisplayValue(column, value)}
          <Tooltip title={lockedReason}><LockIcon sx={{ fontSize: 13, color: 'text.disabled' }} /></Tooltip>
        </Box>
      )
      : formatDisplayValue(column, value);
  return (
    <>
      <TableCell
        onClick={startEdit}
        sx={{
          cursor: !editable || isCategoryReadOnly || (column.readOnly && !isEditableDimensionsCell) ? 'default' : isCategoryType || isEditableDimensionsCell || column.type === 'select' ? 'pointer' : 'text',
          width: column.width,
          maxWidth: column.type === 'text' ? 240 : 160,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: column.type === 'text' ? 'normal' : 'nowrap',
          fontSize: '0.85rem',
          py: 0.75,
          borderRadius: isEditableCell ? 1 : 0,
          boxShadow: dirty ? 'inset 0 0 0 1px rgba(255, 167, 38, 0.7)' : 'none',
          bgcolor: dirty ? 'rgba(255, 167, 38, 0.14)' : undefined,
          '&:hover': isEditableCell ? { bgcolor: dirty ? 'rgba(255, 167, 38, 0.22)' : 'rgba(25, 118, 210, 0.08)', boxShadow: 'inset 0 0 0 1.5px rgba(25, 118, 210, 0.6)' } : {},
        }}
      >
        {displayValue}
      </TableCell>
      {isDimensionsField && dimsAnchorEl && (
        <DimensionsEditPopover
          anchorEl={dimsAnchorEl}
          initial={value}
          onClose={() => setDimsAnchorEl(null)}
          onSave={(dims) => { setDimsAnchorEl(null); onSave(dims); }}
        />
      )}
    </>
  );
}

const LENGTH_UNITS = ['in', 'cm', 'ft', 'm'];
const WEIGHT_UNITS = ['lb', 'oz', 'kg', 'g'];
// Older web edits stored long unit names; the pipeline and channel posts use these short forms.
const normalizeUnit = (u, fallback) => ({ inch: 'in', inches: 'in', pound: 'lb', pounds: 'lb', lbs: 'lb' }[u] || u || fallback);

// "140 × 135 × 50 cm · 0.5 lb" for a gptDimensionsData object.
export function formatDimensions(d) {
  if (!d || typeof d !== 'object') return '';
  const parts = [];
  const sides = [d.length, d.width, d.height].filter((v) => v !== '' && v != null);
  if (sides.length) parts.push(`${sides.join(' × ')} ${normalizeUnit(d.dimensionsUnit, 'in')}`);
  if (d.weight) parts.push(`${d.weight} ${normalizeUnit(d.weightUnit, 'lb')}`);
  return parts.join(' · ');
}

function DimensionsEditPopover({ anchorEl, initial, onClose, onSave }) {
  const d = initial && typeof initial === 'object' ? initial : {};
  const [length, setLength] = useState(d.length || '');
  const [width, setWidth] = useState(d.width || '');
  const [height, setHeight] = useState(d.height || '');
  const [weight, setWeight] = useState(d.weight || '');
  const [dimensionsUnit, setDimensionsUnit] = useState(normalizeUnit(d.dimensionsUnit, 'in'));
  const [weightUnit, setWeightUnit] = useState(normalizeUnit(d.weightUnit, 'lb'));

  const handleSave = () => {
    onSave({
      ...d,
      length: String(length).trim(),
      width: String(width).trim(),
      height: String(height).trim(),
      weight: String(weight).trim(),
      dimensionsUnit,
      weightUnit,
      // Hand-entered, so fully confident.
      confidencePercent: '1.0',
    });
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <Box sx={{ p: 1.5, width: 300, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Typography variant="subtitle2">Dimensions</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField label="Length" size="small" type="number" value={length} onChange={(e) => setLength(e.target.value)} autoFocus />
          <TextField label="Width" size="small" type="number" value={width} onChange={(e) => setWidth(e.target.value)} />
          <TextField label="Height" size="small" type="number" value={height} onChange={(e) => setHeight(e.target.value)} />
        </Box>
        <Select size="small" value={dimensionsUnit} onChange={(e) => setDimensionsUnit(e.target.value)}>
          {LENGTH_UNITS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
        </Select>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField label="Weight" size="small" type="number" value={weight} onChange={(e) => setWeight(e.target.value)} sx={{ flex: 1 }} />
          <Select size="small" value={weightUnit} onChange={(e) => setWeightUnit(e.target.value)} sx={{ width: 90 }}>
            {WEIGHT_UNITS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
          </Select>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button size="small" onClick={onClose}>Cancel</Button>
          <Button size="small" variant="contained" onClick={handleSave}>Done</Button>
        </Box>
      </Box>
    </Popover>
  );
}

function SoldBadge() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'text.disabled' }} />
      <span style={{ fontSize: '0.78rem', color: '#999' }}>Sold</span>
    </Box>
  );
}

const PendingSpinner = () => <CircularProgress size={18} thickness={4} />;

function ComputedCellContent({ column, value, product, onClick, onPost, onUpdate, onDelete, isPending = false }) {
  const isSoldOut = product?.quantity === 0;

  if (column.id === 'shopify') {
    if (isPending) return <PendingSpinner />;
    if (isSoldOut) return <SoldBadge />;
    const arr = Array.isArray(value) ? value : [];
    const url = arr[0]?.uri;
    if (url) {
      return (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button size="small" variant="outlined" color="success" onClick={() => window.open(url, '_blank')}
            sx={{ fontSize: '0.75rem', py: 0.25, minWidth: 50 }}>
            View
          </Button>
          <Button size="small" variant="outlined" color="info" onClick={onUpdate}
            sx={{ fontSize: '0.75rem', py: 0.25, minWidth: 50 }}>
            Update
          </Button>
          <Button size="small" variant="outlined" color="error" onClick={onDelete}
            sx={{ fontSize: '0.75rem', py: 0.25, minWidth: 50 }}>
            Delete
          </Button>
        </Box>
      );
    }
    return (
      <Button size="small" variant="outlined" onClick={onPost}
        sx={{ fontSize: '0.75rem', py: 0.25, minWidth: 60 }}>
        Post
      </Button>
    );
  }

  if (column.id === 'ebay') {
    if (isPending) return <PendingSpinner />;
    if (isSoldOut) return <SoldBadge />;
    const arr = Array.isArray(value) ? value : [];
    const listingId = arr[0]?.listingId;
    const url = listingId ? `${process.env.REACT_APP_EBAY_PRODUCT_URL}/${listingId}` : null;
    if (url) {
      return (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button size="small" variant="outlined" color="primary" onClick={() => window.open(url, '_blank')}
            sx={{ fontSize: '0.75rem', py: 0.25, minWidth: 50 }}>
            View
          </Button>
          <Button size="small" variant="outlined" color="info" onClick={onUpdate}
            sx={{ fontSize: '0.75rem', py: 0.25, minWidth: 50 }}>
            Update
          </Button>
          <Button size="small" variant="outlined" color="error" onClick={onDelete}
            sx={{ fontSize: '0.75rem', py: 0.25, minWidth: 50 }}>
            Delete
          </Button>
        </Box>
      );
    }
    return (
      <Button size="small" variant="outlined" color="primary" onClick={onPost}
        sx={{ fontSize: '0.75rem', py: 0.25, minWidth: 60 }}>
        Post
      </Button>
    );
  }

  if (column.id === 'square') {
    if (isPending) return <PendingSpinner />;
    if (isSoldOut) return <SoldBadge />;
    const arr = Array.isArray(value) ? value : [];
    const itemId = arr[0]?.itemId;
    if (itemId) {
      return (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Tooltip title={itemId}>
            <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main' }} />
          </Tooltip>
          <Button size="small" variant="outlined" color="info" onClick={onUpdate}
            sx={{ fontSize: '0.75rem', py: 0.25, minWidth: 50 }}>
            Update
          </Button>
          {!product?.importedFromSquare && (
            <Button size="small" variant="outlined" color="error" onClick={onDelete}
              sx={{ fontSize: '0.75rem', py: 0.25, minWidth: 50 }}>
              Delete
            </Button>
          )}
        </Box>
      );
    }
    return (
      <Button size="small" variant="outlined" color="success" onClick={onPost}
        sx={{ fontSize: '0.75rem', py: 0.25, minWidth: 60 }}>
        Post
      </Button>
    );
  }

  if (column.id === 'etsy') {
    if (isPending) return <PendingSpinner />;
    if (isSoldOut) return <SoldBadge />;
    const arr = Array.isArray(value) ? value : [];
    const listingId = arr[0]?.listingId;
    if (listingId) {
      return (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button size="small" variant="outlined" color="warning" onClick={() => window.open(`https://www.etsy.com/listing/${listingId}`, '_blank')}
            sx={{ fontSize: '0.75rem', py: 0.25, minWidth: 50 }}>
            View
          </Button>
          <Button size="small" variant="outlined" color="info" onClick={onUpdate}
            sx={{ fontSize: '0.75rem', py: 0.25, minWidth: 50 }}>
            Update
          </Button>
          <Button size="small" variant="outlined" color="error" onClick={onDelete}
            sx={{ fontSize: '0.75rem', py: 0.25, minWidth: 50 }}>
            Delete
          </Button>
        </Box>
      );
    }
    return (
      <Button size="small" variant="outlined" color="warning" onClick={onPost}
        sx={{ fontSize: '0.75rem', py: 0.25, minWidth: 60 }}>
        Post
      </Button>
    );
  }

  if (column.id === 'shopifyLink') {
    const url = Array.isArray(value) ? value[0]?.uri : null;
    if (!url) return <span style={{ color: '#bbb', fontSize: '0.85rem' }}>—</span>;
    return (
      <a href={url} target="_blank" rel="noreferrer" style={{ fontSize: '0.8rem', color: '#1976d2', wordBreak: 'break-all' }}>
        {url.replace(/^https?:\/\//, '')}
      </a>
    );
  }

  if (column.id === 'bodyHtml') {
    const hasHtml = !!(value && value.length > 0);
    return (
      <span onClick={onClick} style={{ fontSize: '0.8rem', color: hasHtml ? '#1976d2' : '#aaa', cursor: onClick ? 'pointer' : 'default' }}>
        {hasHtml ? 'View Preview ›' : 'No Preview'}
      </span>
    );
  }

  if (column.id === 'allProductInfo') {
    return <AllProductInfoCell product={product} />;
  }

  if (column.id === 'otherPrices') {
    return <OtherPricesCell value={value} />;
  }

  if (column.id === 'shopifyAdminLink') {
    const arr = Array.isArray(value) ? value : [];
    const item = arr[0];
    if (!item?.uri || !item?.id) return <span style={{ color: '#bbb', fontSize: '0.85rem' }}>—</span>;
    let adminUrl = '';
    try {
      const domain = new URL(item.uri).hostname;
      const numericId = item.id.split('/').pop();
      adminUrl = `https://${domain}/admin/products/${numericId}`;
    } catch {
      return <span style={{ color: '#bbb', fontSize: '0.85rem' }}>—</span>;
    }
    return (
      <Button size="small" variant="outlined" onClick={() => window.open(adminUrl, '_blank')}
        sx={{ fontSize: '0.75rem', py: 0.25, minWidth: 60 }}>
        Admin
      </Button>
    );
  }

  return <span style={{ fontSize: '0.85rem' }}>{value || '—'}</span>;
}

function formatDisplayValue(column, value) {
  if (value == null || value === '') return <span style={{ color: '#bbb' }}>—</span>;
  if (column.id === 'condition') {
    return conditionDisplayLabel(value);
  }
  if (column.type === 'date') {
    try { return formattedDate(value); } catch { return value; }
  }
  if (column.type === 'currency') {
    const clean = typeof value === 'string' ? value.replace(/^\$/, '') : value;
    return `$${clean}`;
  }
  if (column.type === 'percent') {
    const pct = Math.round(parseFloat(value) * 100);
    return isNaN(pct) ? value : `${pct}%`;
  }
  return value;
}

function AllProductInfoCell({ product }) {
  const [open, setOpen] = useState(false);
  const specs = product?.specs || {};
  const entries = Object.entries(specs).filter(([, v]) => v != null && v !== '');
  return (
    <>
      <Button size="small" variant="outlined" onClick={() => setOpen(true)}
        sx={{ fontSize: '0.75rem', py: 0.25, minWidth: 50 }}>
        View
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem' }}>{product?.title || 'Product Info'}</DialogTitle>
        <DialogContent dividers>
          {entries.length === 0
            ? <Typography variant="body2" color="text.secondary">No data</Typography>
            : entries.map(([k, v]) => (
              <Box key={k} sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 160, color: 'text.secondary' }}>{k}</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{String(v)}</Typography>
              </Box>
            ))
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} size="small">Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function OtherPricesCell({ value }) {
  const [anchorEl, setAnchorEl] = useState(null);
  if (!value || typeof value !== 'object') return <span style={{ color: '#bbb', fontSize: '0.85rem' }}>—</span>;
  const entries = Object.entries(value).filter(([, v]) => v != null && v !== '');
  return (
    <>
      <Button size="small" variant="outlined" onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ fontSize: '0.75rem', py: 0.25, minWidth: 60 }}>
        Prices
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, minWidth: 240 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Price Options</Typography>
          {entries.length === 0
            ? <Typography variant="body2" color="text.secondary">No pricing data</Typography>
            : entries.map(([k, v]) => (
              <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{k}</Typography>
                <Typography variant="body2">{String(v)}</Typography>
              </Box>
            ))
          }
        </Box>
      </Popover>
    </>
  );
}
